import { BaseResponse } from "../../../base/response/BaseResponse";
import { IConnectRender } from "../../definitions/IConnectRender";
import { IListRender } from "../../definitions/list/IListRender";
import { ListConfig } from "../../definitions/list/ListConfig";
import { SectionRender } from "../section/SectionRender";

/** Contador para ids únicos por instancia. */
let LIST_ID = 0;

/** Sección de tipo lista: extiende {@link SectionRender} aportando como contenido
 *  una card cuyas filas son **componentes propios** (`config.createItem`). Carga
 *  los datos async con estados cargando/vacío/error y expone `reload()`. */
export class ListRender<T extends BaseResponse>
  extends SectionRender
  implements IListRender
{
  /** Id del contenedor de filas (único por instancia). */
  private readonly _rowsId: string;

  /** @param _config Cabecera de sección + servicio, factory de fila y mensaje vacío. */
  constructor(private readonly _config: ListConfig<T>) {
    super(_config);
    LIST_ID += 1;
    this._rowsId = `list-rows-${LIST_ID}`;
  }

  /** Contenido: card con el contenedor de filas. */
  protected renderContent(): string {
    return `<div class="sx-list-card"><div class="sx-list-rows" id="${this._rowsId}"></div></div>`;
  }

  /** Captura el contenedor e inicia la carga. */
  protected connectContent(): void {
    this._rowsEl = document.getElementById(this._rowsId);
    void this._load();
  }

  /** Marca disposed y dispone las filas. */
  protected disposeContent(): void {
    this._disposed = true;
    this._disposeItems();
  }

  /** Recarga la lista (tras un alta/edición/borrado). */
  reload(): Promise<void> {
    return this._load();
  }

  /** Contenedor de filas (disponible tras `connect`). */
  private _rowsEl: HTMLElement | null = null;
  /** Componentes-fila vivos. */
  private _items: IConnectRender[] = [];
  /** `true` tras `dispose()`: corta el render si la carga termina después. */
  private _disposed = false;

  /** Carga datos y pinta filas o el estado correspondiente. */
  private async _load(): Promise<void> {
    if (!this._rowsEl) return;
    this._setMessage("Cargando…");
    try {
      const items = await this._config.listService.getListAsync();
      if (this._disposed) return;
      this._renderItems(items);
    } catch {
      if (this._disposed) return;
      this._setMessage("Error al cargar la información.");
    }
  }

  /** Crea, inyecta y conecta un componente-fila por item (o el mensaje vacío). */
  private _renderItems(items: T[]): void {
    this._disposeItems();
    if (!this._rowsEl) return;
    if (items.length === 0) {
      this._setMessage(this._config.emptyMessage ?? "Sin registros");
      return;
    }
    this._items = items.map((item) => this._config.createItem(item));
    this._rowsEl.innerHTML = this._items.map((c) => c.render()).join("");
    this._items.forEach((c) => c.connect());
  }

  /** Dispone y descarta las filas vivas. */
  private _disposeItems(): void {
    this._items.forEach((c) => c.dispose());
    this._items = [];
  }

  /** Muestra un mensaje de estado (cargando/vacío/error). */
  private _setMessage(text: string): void {
    if (this._rowsEl) {
      this._rowsEl.innerHTML = `<div class="sx-list-empty">${text}</div>`;
    }
  }
}
